import React, { useEffect, useMemo, useState } from 'react'
import Joyride, { CallBackProps, Step, STATUS, Props } from 'react-joyride'

interface BasicGuidedTourMethods {
	startTour: () => void
	stopTour: () => void
}

interface BasicGuideTourProps extends Props {
	steps: Step[]
	bind: (methods: BasicGuidedTourMethods) => void
}

const BasicGuidedTour = ({ steps, bind, ...props }: BasicGuideTourProps) => {
	const [run, setRun] = useState(false)
	const [currentSteps, setCurrentSteps] = useState<Step[]>(steps)

	const memoizedSteps = useMemo(() => steps, [steps])

	useEffect(() => {
		setCurrentSteps(memoizedSteps)
	}, [memoizedSteps])

	const stopTour = () => setRun(false)

	useEffect(() => {
		bind({
			startTour: () => setRun(true),
			stopTour,
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleJoyrideCallback = (data: CallBackProps) => {
		const { status } = data

		const finishedStatus: string[] = [STATUS.FINISHED, STATUS.SKIPPED]

		if (finishedStatus.includes(status)) {
			stopTour()
		}
	}

	useEffect(() => {
		const handleOverlayClick = (e: MouseEvent) => {
			const target = e.target as HTMLElement

			if (target.classList.contains('react-joyride__overlay')) {
				stopTour()
			}
		}

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' || e.keyCode === 27) {
				stopTour()
			}
		}

		document.addEventListener('click', handleOverlayClick)
		document.addEventListener('keydown', handleKeyDown)

		return () => {
			document.removeEventListener('click', handleOverlayClick)
			document.removeEventListener('keydown', handleKeyDown)
		}
	}, [])

	return (
		<div className=''>
			<Joyride
				{...props}
				callback={handleJoyrideCallback}
				key='joyride-static-key'
				steps={currentSteps}
				run={run}
				continuous
				scrollOffset={64}
				scrollToFirstStep
				hideCloseButton
				styles={{
					options: {
						zIndex: 10000,
					},
					buttonNext: {
						background: '#6BbBDC',
					},
					buttonBack: {
						color: '#6BbBDC',
						marginRight: '62%',
					},
				}}
			/>
		</div>
	)
}

export const useBasicGuidedTour = () => {
	const [tourInstance, setTourInstance] =
		useState<BasicGuidedTourMethods | null>(null)

	const bind = (methods: BasicGuidedTourMethods) => {
		setTourInstance(methods)
	}

	return {
		startTour: () => tourInstance?.startTour(),
		stopTour: () => tourInstance?.stopTour(),
		bind,
	}
}

export default BasicGuidedTour
